import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiOutlineChartBar, HiOutlineUsers, HiOutlineAcademicCap, HiOutlineChatBubbleLeftRight } from 'react-icons/hi2';
import './FAQSection.css';

const faqData = [
  {
    question: "What is Stocks4u?",
    answer: "Stocks4u is an AI-powered trading intelligence platform that helps you watch, analyse and trade smarter. We provide real-time market insights, advanced analytics and actionable data to help you stay ahead.",
    icon: HiOutlineChartBar
  },
  {
    question: "Who can use Stocks4u?",
    answer: "Whether you are an elite alpha trader, a long-term investor, or a beginner looking for a powerful tool, Stocks4U provides the real-time screeners, charts, and technical indicators you need to analyze the market with confidence.",
    icon: HiOutlineUsers
  },
  {
    question: "Does Stocks4u offer educational resources for beginners?",
    answer: "Yes, Stocks4U provides intuitive educational tools, advanced technical indicators, and comprehensive market data that make it easy for beginners to learn and analyze market trends effectively.",
    icon: HiOutlineAcademicCap
  }
];

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="s4u-faq-section">
      {/* Background Effects */}
      <div className="s4u-faq-bg-grid" />
      <div className="s4u-faq-bg-glow" />
      <div className="s4u-faq-bg-glow-2" />
      <div className="s4u-faq-bg-noise" />
      <div className="s4u-faq-bg-curve s4u-faq-bg-curve-1" />
      <div className="s4u-faq-bg-curve s4u-faq-bg-curve-2" />

      <div className="s4u-faq-inner">
        {/* Section Header */}
        <motion.div
          className="s4u-faq-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="s4u-faq-pill">
            <span className="s4u-faq-pill-dot" />
            FAQ
          </div>

          <h2 className="s4u-faq-heading">
            Your questions, <span className="s4u-faq-heading-gradient">answered here.</span>
          </h2>

          <p className="s4u-faq-subtext">
            Everything you need to know about Stocks4u.
          </p>

          <div className="s4u-faq-divider">
            <div className="s4u-faq-divider-line" />
          </div>
        </motion.div>

        {/* FAQ List */}
        <div className="s4u-faq-list">
          {faqData.map((faq, index) => {
            const isOpen = openIndex === index;
            const IconComponent = faq.icon;
            return (
              <motion.div
                key={index}
                className={`s4u-faq-card ${isOpen ? 's4u-faq-card-open' : ''}`}
                onClick={() => toggleFAQ(index)}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.12, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                layout
              >
                <div className="s4u-faq-card-inner">
                  <div className="s4u-faq-card-header">
                    <div className="s4u-faq-icon-wrap">
                      <IconComponent className="s4u-faq-icon" />
                    </div>
                    <h3 className="s4u-faq-question">{faq.question}</h3>
                    <div className={`s4u-faq-toggle ${isOpen ? 's4u-faq-toggle-open' : ''}`}>
                      <span className="s4u-faq-toggle-bar s4u-faq-toggle-h" />
                      <span className="s4u-faq-toggle-bar s4u-faq-toggle-v" />
                    </div>
                  </div>

                  <AnimatePresence mode="wait">
                    {isOpen && (
                      <motion.div
                        className="s4u-faq-answer-wrap"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <div className="s4u-faq-answer-inner">
                          <div className="s4u-faq-answer-indicator" />
                          <p className="s4u-faq-answer">{faq.answer}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="s4u-faq-cta"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="s4u-faq-cta-inner">
            <div className="s4u-faq-cta-left">
              <div className="s4u-faq-cta-icon-wrap">
                <HiOutlineChatBubbleLeftRight className="s4u-faq-cta-icon" />
              </div>
              <div className="s4u-faq-cta-text">
                <span className="s4u-faq-cta-title">Still have questions?</span>
                <span className="s4u-faq-cta-sub">Our support team is here to help you.</span>
              </div>
            </div>
            <a href="mailto:support@stocks4u.in" className="s4u-faq-cta-btn">
              Contact Support
              <span className="s4u-faq-cta-btn-arrow">→</span>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;
